import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import { APIProvider, Map, AdvancedMarker, Pin, useMap } from "@vis.gl/react-google-maps";
import Loading from "../components/Loading";
import Modal from "../components/Modal";

function MapController({ center, routeDetail, showPath, showRealPath, realPathLocations, driverLocations }) {
    const map = useMap();
    const hasFitted = useRef(false);

    useEffect(() => {
        if (!map || hasFitted.current) return;
        const google = window.google;
        if (!google?.maps) return;

        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;

        if (driverLocations) {
            Object.values(driverLocations).forEach((loc) => {
                bounds.extend({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
                hasPoints = true;
            });
        }

        if (routeDetail?.point_visits) {
            routeDetail.point_visits.forEach((pv) => {
                bounds.extend({ lat: Number(pv.point.latitude), lng: Number(pv.point.longitude) });
                hasPoints = true;
            });
        }

        if (routeDetail?.center) {
            bounds.extend({ lat: Number(routeDetail.center.latitude), lng: Number(routeDetail.center.longitude) });
            hasPoints = true;
        }

        if (showPath && routeDetail?.route?.estimated_path && google.maps.geometry) {
            const decodedPath = google.maps.geometry.encoding.decodePath(routeDetail.route.estimated_path);
            decodedPath.forEach((p) => bounds.extend(p));
        }

        if (showRealPath && realPathLocations.length > 0) {
            realPathLocations.forEach((loc) => {
                bounds.extend({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
            });
        }

        if (hasPoints) {
            map.fitBounds(bounds, 40);
            hasFitted.current = true;
        } else if (center) {
            map.panTo(center);
        }
    }, [map, center, routeDetail, showPath, showRealPath, realPathLocations, driverLocations]);

    return null;
}

function DecodedPolyline({ path, visible }) {
    const map = useMap();

    useEffect(() => {
        if (!map || !visible || !path) return;
        const google = window.google;
        if (!google?.maps?.geometry) return;

        const decodedPath = google.maps.geometry.encoding.decodePath(path);
        const polyline = new google.maps.Polyline({
            path: decodedPath,
            strokeColor: "#9FA6B2",
            strokeOpacity: 0.5,
            strokeWeight: 4,
            icons: [{
                icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.5, strokeColor: "#9FA6B2", fillColor: "#9FA6B2", fillOpacity: 0.8 },
                offset: "0",
                repeat: "100px",
            }],
            map,
        });

        return () => polyline.setMap(null);
    }, [map, path, visible]);

    return null;
}

function LocationsPolyline({ locations, visible }) {
    const map = useMap();

    useEffect(() => {
        if (!map || !visible || !locations || locations.length === 0) return;
        const google = window.google;
        if (!google?.maps) return;

        const path = locations.map((loc) => ({
            lat: Number(loc.latitude),
            lng: Number(loc.longitude),
        }));

        const polyline = new google.maps.Polyline({
            path,
            strokeColor: "#3B71CA",
            strokeOpacity: 0.7,
            strokeWeight: 4,
            icons: [{
                icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.5, strokeColor: "#3B71CA", fillColor: "#3B71CA", fillOpacity: 0.8 },
                offset: "0",
                repeat: "100px",
            }],
            map,
        });

        return () => polyline.setMap(null);
    }, [map, locations, visible]);

    return null;
}

const STATUS_COLORS = {
    meets_standards: { bg: "#0d6efd", glyph: "#fff", border: "#0d6efd" },
    below_standards: { bg: "#ffc107", glyph: "#000", border: "#ffc107" },
    no_tasks_completed: { bg: "#dc3545", glyph: "#fff", border: "#dc3545" },
};

const TYPES_LABELS = {
    "locker": "consigne",
    "parcel_shop": "commerce"
};

const STATUS_LABELS = {
    in_progress: "en cours",
    not_started: "en attente",
    completed: "terminé",
    failed: "échoué"
};

const STATUS_COLORS2 = {
    in_progress: "primary",
    not_started: "secondary",
    completed: "success",
    failed: "danger"
};

const ISSUES_LABELS = {
    dropoff_issues_full_locker: "la consigne est pleine",
    dropoff_issues_oversized: "le colis est trop volumineux",
    electronics_compartment_failed_to_open: "le compartement électronique ne s'est pas ouvert",
    store_is_closed: "le magasin est fermé",
    scanner_does_not_work: "la scanner ne fonctionne pas"
}


function getStatusColor(status) {
    return STATUS_COLORS[status] || { bg: "#6c757d", glyph: "#fff", border: "#6c757d" };
}

function formatTime(isoString) {
    if (!isoString) return "";
    return moment(isoString).format("HH:mm");
}

function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h${m.toString().padStart(2, "0")}`;
    return `${m} min`;
}

export default function RoutePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [routeDetail, setRouteDetail] = useState(null);
    const [showPath, setShowPath] = useState(false);
    const [showRealPath, setShowRealPath] = useState(false);
    const [realPathLocations, setRealPathLocations] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [driverLocations, setDriverLocations] = useState({});
    const [showProblemsModal, setShowProblemsModal] = useState(false);
    const [selectedProblems, setSelectedProblems] = useState([]);

    const defaultCenter = { lat: 48.8566, lng: 2.3522 };

    const fetchRoute = useCallback(async (background = false) => {
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/drivers/${id}`);
            if (!response.ok) {
                if (!background) setError("Aucune route disponible");
                return;
            }
            const data = await response.json();
            setRouteDetail(data);
            if (data.route.delivery_status === "in_progress") {
                try {
                    const locResponse = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/drivers/locations`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ routeIds: [data.route.id], lastLocation: true }),
                    });
                    if (locResponse.ok) {
                        const locData = await locResponse.json();
                        setDriverLocations(locData);
                    }
                } catch (err) {
                    console.error("Erreur lors du chargement des positions livreurs", err);
                }
            }
        } catch (err) {
            if (!background) setError("Erreur lors du chargement de cette route.");
        } finally {
            if (!background) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchRoute();
        const interval = setInterval(() => fetchRoute(true), 15000);
        return () => clearInterval(interval);
    }, [fetchRoute]);

    async function handleRealPathToggle(checked) {
        setShowRealPath(checked);
        if (checked && routeDetail) {
            try {
                const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/drivers/locations`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ routeIds: [routeDetail.route.id], lastLocation: false }),
                });
                if (response.ok) {
                    const data = await response.json();
                    setRealPathLocations(data[routeDetail.route.id] || []);
                }
            } catch (err) {
                console.error("Erreur lors du chargement du trajet réel", err);
            }
        } else {
            setRealPathLocations([]);
        }
    }

    function handleBack() {
        navigate("/routes", { state: { date: location.state?.date } });
    }

    const mapCenter = routeDetail?.center
        ? { lat: Number(routeDetail.center.latitude), lng: Number(routeDetail.center.longitude) }
        : null;

    return (
        <>
            <Modal
                title={"Problèmes"}
                body={
                    <>
                        {selectedProblems.map((problem, i) => (
                            <div key={i} className={i > 0 ? "mt-3 pt-3 border-top" : ""}>
                                <p className="mb-1"><strong>Raison :</strong> {ISSUES_LABELS[problem.reason] || problem.reason}</p>
                                <p className="mb-1 text-muted small">
                                    {moment(problem.created_at).format("dddd DD MMMM YYYY [à] HH:mm")}
                                </p>
                                {problem.media?.length > 0 && problem.media.map((m, j) => (
                                    <img
                                        key={j}
                                        src={m.content_url}
                                        alt={`Problème ${i + 1}`}
                                        className="img-fluid rounded mt-2"
                                        style={{ maxHeight: 300 }}
                                    />
                                ))}
                            </div>
                        ))}
                    </>
                }
                button={{
                    close: "Fermer",
                    valid: "OK"
                }}
                showModal={showProblemsModal}
                setShowModal={setShowProblemsModal}
                onSubmit={() => setShowProblemsModal(false)}
            />
        
        <div className="card mb-4">
            <div className="card-header py-3">
                <div className="row py-3">
                    <div className="col-sm-6 text-center text-sm-start">
                        <h5 className="mb-4 mb-sm-0 mt-2">
                            Opération
                        </h5>
                    </div>
                    <div className="col-sm-6 text-center text-sm-end">
                        <button
                            type="button"
                            className="btn btn-outline-primary me-2"
                            onClick={handleBack}
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Retour aux routes
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                {loading ? (
                    <Loading />
                ) : error ? (
                    <div className="text-danger text-center mb-3">{error}</div>
                ) : routeDetail && (
                    <div className="operation">
                        <section id="route-section">
                            <div>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="mb-0">{routeDetail.route.name} - {routeDetail.driver?.name ?? ""}</h5>
                                    <span className={"badge bg-" + STATUS_COLORS2[routeDetail.route.delivery_status]}>
                                        {STATUS_LABELS[routeDetail.route.delivery_status]}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <small className="text-muted">
                                        {routeDetail.center?.code} | Durée : {formatDuration(routeDetail.route.duration)} ({routeDetail.route.planned_distance/1000} km)
                                    </small>
                                </div>
                                <div className="d-flex justify-content-between gap-3">
                                    <label className="form-check-label d-flex align-items-center gap-2 mb-0">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={showPath}
                                            onChange={(e) => setShowPath(e.target.checked)}
                                        />
                                        Trajet estimé
                                    </label>
                                    <label className="form-check-label d-flex align-items-center gap-2 mb-0">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={showRealPath}
                                            onChange={(e) => handleRealPathToggle(e.target.checked)}
                                        />
                                        Trajet réel
                                    </label>
                                </div>
                                <hr />
                                <div className="point-visits-list">
                                    {routeDetail.point_visits.map((pv) => {
                                        const color = getStatusColor(pv.success_status);
                                        return (
                                            <div className="point-visit-item" key={pv.id}>
                                                <div className="point-visit-sequence">
                                                    <span
                                                        className="point-visit-badge"
                                                        style={{ backgroundColor: color.bg, color: color.glyph }}
                                                    >
                                                        {pv.sequence}
                                                    </span>
                                                </div>
                                                <div className="point-visit-details">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <strong>{pv.point.name}</strong>
                                                            <p className="mb-0 text-muted small">{pv.point.address}, {pv.point.city}</p>
                                                            <p className="mb-0 small">
                                                                {TYPES_LABELS[pv.point.type] + " | " + pv.point.code}
                                                            </p>
                                                        </div>
                                                        <div className="text-end">
                                                            {pv.problems.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className={"badge bg-" + STATUS_COLORS2[pv.delivery_status]}
                                                                    style={{ border: "none", cursor: "pointer" }}
                                                                    onClick={() => {
                                                                        setSelectedProblems(pv.problems);
                                                                        setShowProblemsModal(true);
                                                                    }}
                                                                >
                                                                    {STATUS_LABELS[pv.delivery_status]}
                                                                </button>
                                                            )}
                                                            
                                                        </div>
                                                    </div>
                                                    <div className="mt-1 small text-muted">
                                                        {pv.point.delivery_hours?.length > 0 && (
                                                            <div>
                                                                Heures d'ouverture : {pv.point.delivery_hours.map((h, i) => (
                                                                    <span key={i}>
                                                                        {i > 0 && " / "}
                                                                        {h.opens_at} - {h.closes_at}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <div>ETA : {formatTime(pv.estimated_arrival_time)} - {formatTime(pv.estimated_departure_time)}</div>
                                                        {pv.arrived_at && (
                                                            <div>Réel : {formatTime(pv.arrived_at)} - {formatTime(pv.departed_at)}</div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-3 mt-1 small">
                                                        <span>{pv.shipments.dropoff_completed_count}/{pv.shipments.dropoff_planned_count} dépôts</span>
                                                        <span>{pv.shipments.pickup_completed_count}/{pv.shipments.pickup_total_count} collectes</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                        <section id="map-section">
                            <APIProvider
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                libraries={["geometry"]}
                            >
                                <Map
                                    defaultCenter={defaultCenter}
                                    defaultZoom={12}
                                    mapId="a24a5e7a0ff8f2636489d9a2"
                                    style={{ width: "100%", height: "600px" }}
                                >
                                    <MapController
                                        center={mapCenter}
                                        routeDetail={routeDetail}
                                        showPath={showPath}
                                        showRealPath={showRealPath}
                                        realPathLocations={realPathLocations}
                                        driverLocations={driverLocations}
                                    />
                                    {Object.entries(driverLocations).map(([routeId, loc]) => (
                                        <AdvancedMarker
                                            key={`driver-${routeId}`}
                                            position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }}
                                            title={`${routeDetail.route.name}${routeDetail.driver ? ` - ${routeDetail.driver.name}` : ''}`}
                                        >
                                            <div style={{
                                                background: "#f5f5f5",
                                                color: "black",
                                                borderRadius: "50%",
                                                border: "2px solid #3b71ca",
                                                width: 32,
                                                height: 32,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 9,
                                                fontWeight: "bold",
                                                whiteSpace: "nowrap",
                                            }}>
                                                {routeDetail.route.name}
                                            </div>
                                        </AdvancedMarker>
                                    ))}
                                    {routeDetail.point_visits.map((pv) => {
                                        const color = getStatusColor(pv.success_status);
                                        return (
                                            <AdvancedMarker
                                                key={pv.id}
                                                position={{ lat: Number(pv.point.latitude), lng: Number(pv.point.longitude) }}
                                                title={`${pv.sequence} - ${pv.point.name}`}
                                            >
                                                <div style={{
                                                    background: color.bg,
                                                    color: color.glyph,
                                                    borderRadius: "50%",
                                                    border: "2px solid #e9ebef",
                                                    width: 24,
                                                    height: 24,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 7,
                                                    fontWeight: "bold",
                                                }}>
                                                    {pv.sequence}
                                                </div>
                                            </AdvancedMarker>
                                        );
                                    })}
                                    {routeDetail.center && (
                                        <AdvancedMarker
                                            position={{ lat: Number(routeDetail.center.latitude), lng: Number(routeDetail.center.longitude) }}
                                            title={`Hub - ${routeDetail.center.code}`}
                                        >
                                            <Pin background="#000" glyphColor="#fff" borderColor="#000" glyph="H" />
                                        </AdvancedMarker>
                                    )}
                                    <DecodedPolyline
                                        path={routeDetail.route.estimated_path}
                                        visible={showPath}
                                    />
                                    <LocationsPolyline
                                        locations={realPathLocations}
                                        visible={showRealPath}
                                    />
                                </Map>
                            </APIProvider>
                        </section>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
